import { create } from 'zustand';
import type { Agent, AgentStatusUpdate } from '@/types';

interface AgentFilters {
  status: 'all' | 'online' | 'offline';
  platform: 'all' | 'windows' | 'macos' | 'linux';
  search: string;
}

interface AgentState {
  // State
  agents: Agent[];
  selectedAgentId: string | null;
  isLoading: boolean;
  error: string | null;
  filters: AgentFilters;
  
  // Computed getters
  getAgent: (id: string) => Agent | undefined;
  getFilteredAgents: () => Agent[];
  getOnlineCount: () => number;
  getOfflineCount: () => number;
  
  // Actions
  setAgents: (agents: Agent[]) => void;
  addAgent: (agent: Agent) => void;
  updateAgent: (id: string, updates: Partial<Agent>) => void;
  removeAgent: (id: string) => void;
  updateAgentStatus: (update: AgentStatusUpdate) => void;
  setSelectedAgent: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFilters: (filters: Partial<AgentFilters>) => void;
  clearError: () => void;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  // Initial state
  agents: [],
  selectedAgentId: null,
  isLoading: false,
  error: null,
  filters: {
    status: 'all',
    platform: 'all',
    search: '',
  },
  
  // Computed getters
  getAgent: (id) => get().agents.find((a) => a.id === id),
  
  getFilteredAgents: () => {
    const { agents, filters } = get();
    return agents.filter((agent) => {
      // Status filter
      if (filters.status === 'online' && !agent.isOnline) return false;
      if (filters.status === 'offline' && agent.isOnline) return false;
      
      // Platform filter
      if (filters.platform !== 'all' && agent.platform !== filters.platform) return false;
      
      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const matchesSubdomain = agent.subdomain.toLowerCase().includes(search);
        const matchesLabel = agent.label?.toLowerCase().includes(search);
        if (!matchesSubdomain && !matchesLabel) return false;
      }
      
      return true;
    });
  },
  
  getOnlineCount: () => get().agents.filter((a) => a.isOnline).length,
  
  getOfflineCount: () => get().agents.filter((a) => !a.isOnline).length,
  
  // Actions
  setAgents: (agents) => set({ agents, isLoading: false }),
  
  addAgent: (agent) => set((state) => ({
    agents: [...state.agents, agent],
  })),
  
  updateAgent: (id, updates) => set((state) => ({
    agents: state.agents.map((a) =>
      a.id === id ? { ...a, ...updates } : a
    ),
  })),
  
  removeAgent: (id) => set((state) => ({
    agents: state.agents.filter((a) => a.id !== id),
    selectedAgentId: state.selectedAgentId === id ? null : state.selectedAgentId,
  })),
  
  updateAgentStatus: (update) => set((state) => ({
    agents: state.agents.map((a) =>
      a.id === update.agentId
        ? { ...a, isOnline: update.isOnline, lastSeenAt: update.lastSeenAt }
        : a
    ),
  })),
  
  setSelectedAgent: (id) => set({ selectedAgentId: id }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error, isLoading: false }),
  
  setFilters: (filters) => set((state) => ({
    filters: { ...state.filters, ...filters },
  })),
  
  clearError: () => set({ error: null }),
}));
